import { connect, Connection, RemoteProxy, WindowMessenger } from 'penpal';
import type {
  ParentAutomatedParticipantAPI,
  PenpalBridgeOptions,
  AutomatedParticipantScaffoldConfig,
  BearerToken,
  TwilioTestToken,
  ParticipantLogEntry
} from './types';

const DEFAULT_PENPAL_TIMEOUT_MS = 5000;
const DEFAULT_PENPAL_MAX_RETRIES = 5;
const TOKEN_REFRESH_BUFFER_MS = 60_000;

type CachedToken<T> = {
  payload: T;
};

/**
 * Manages secure communication between iframe (patient) and parent (host) using Penpal.
 * Handles token fetching, logging, and configuration retrieval.
 */
export class PenpalParticipantBridge {
  private readonly options: Required<
    Omit<PenpalBridgeOptions, 'allowedOrigins' | 'directBearerToken' | 'directTwilioToken'>
  > &
    Pick<PenpalBridgeOptions, 'allowedOrigins' | 'directBearerToken' | 'directTwilioToken'>;

  private readonly participantConfig?: AutomatedParticipantScaffoldConfig;

  private connection?: Connection<ParentAutomatedParticipantAPI>;

  private messenger?: WindowMessenger;

  private api?: RemoteProxy<ParentAutomatedParticipantAPI>;

  private cachedBearerToken?: CachedToken<BearerToken>;

  private cachedTwilioToken?: CachedToken<TwilioTestToken>;

  constructor(options?: PenpalBridgeOptions, config?: AutomatedParticipantScaffoldConfig) {
    this.options = {
      timeoutMs: options?.timeoutMs ?? DEFAULT_PENPAL_TIMEOUT_MS,
      maxRetries: options?.maxRetries ?? DEFAULT_PENPAL_MAX_RETRIES,
      allowedOrigins: options?.allowedOrigins,
      directBearerToken: options?.directBearerToken,
      directTwilioToken: options?.directTwilioToken
    };
    this.participantConfig = config;
  }

  /**
   * Disconnects the Penpal connection and clears cached tokens.
   */
  async disconnect() {
    this.connection?.destroy();
    this.messenger?.destroy();
    this.connection = undefined;
    this.messenger = undefined;
    this.api = undefined;
    this.cachedBearerToken = undefined;
    this.cachedTwilioToken = undefined;
  }

  /**
   * Fetches the VL bearer token from the parent window.
   * Uses cached token if available and not expired, unless force is true.
   */
  async getVlBearerToken(force = false): Promise<BearerToken> {
    if (this.options.directBearerToken) {
      return {
        token: this.options.directBearerToken,
        expiresAt: Date.now() + TOKEN_REFRESH_BUFFER_MS * 2
      };
    }
    if (!force && !this.isTokenExpired(this.cachedBearerToken?.payload)) {
      return this.cachedBearerToken!.payload;
    }
    const api = await this.ensureConnected();
    const token = await api.getVlBearerToken();
    this.cachedBearerToken = { payload: token };
    return token;
  }

  /**
   * Fetches a Twilio test token for the patient participant.
   * Uses cached token if available and not expired, unless force is true.
   */
  async getTwilioTestToken(force = false): Promise<TwilioTestToken> {
    if (this.options.directTwilioToken) {
      return {
        token: this.options.directTwilioToken,
        roomName: this.participantConfig?.participantId ?? 'direct-room',
        expiresAt: Date.now() + TOKEN_REFRESH_BUFFER_MS * 2
      };
    }
    if (!force && !this.isTokenExpired(this.cachedTwilioToken?.payload)) {
      return this.cachedTwilioToken!.payload;
    }
    const api = await this.ensureConnected();
    const token = await api.getTwilioTestToken();
    this.cachedTwilioToken = { payload: token };
    return token;
  }

  /**
   * Logs an event to the parent window.
   */
  async logEvent(entry: ParticipantLogEntry) {
    const api = await this.ensureConnected();
    return api.logEvent(entry);
  }

  /**
   * Retrieves participant configuration from the parent window.
   */
  async getParticipantConfig(): Promise<Record<string, unknown> | undefined> {
    const api = await this.ensureConnected();
    return api.getParticipantConfig();
  }

  async connect(
    childMethods?: Record<string, (...args: unknown[]) => unknown>
  ): Promise<RemoteProxy<ParentAutomatedParticipantAPI>> {
    if (this.api) {
      return this.api;
    }

    this.messenger = this.buildMessenger();
    this.connection = connect<ParentAutomatedParticipantAPI>({
      messenger: this.messenger,
      methods: childMethods ?? {},
      timeout: this.options.timeoutMs
    });

    let attempt = 0;
    while (true) {
      try {
        this.api = await this.connection.promise;
        return this.api;
      } catch (error) {
        attempt += 1;
        if (attempt >= this.options.maxRetries) {
          this.messenger.destroy();
          this.connection.destroy();
          this.messenger = undefined;
          this.connection = undefined;
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, attempt * 250));
      }
    }
  }

  private isTokenExpired(token?: BearerToken | TwilioTestToken) {
    if (!token) return true;
    return Date.now() + TOKEN_REFRESH_BUFFER_MS >= token.expiresAt;
  }

  private async ensureConnected(): Promise<RemoteProxy<ParentAutomatedParticipantAPI>> {
    if (this.api) {
      return this.api;
    }
    this.messenger = this.buildMessenger();
    this.connection = connect<ParentAutomatedParticipantAPI>({
      messenger: this.messenger,
      timeout: this.options.timeoutMs
    });

    let attempt = 0;
    while (true) {
      try {
        this.api = await this.connection.promise;
        return this.api;
      } catch (error) {
        attempt += 1;
        if (attempt >= this.options.maxRetries) {
          this.messenger.destroy();
          this.connection.destroy();
          this.messenger = undefined;
          this.connection = undefined;
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, attempt * 250));
      }
    }
  }

  private buildMessenger() {
    if (globalThis.window === undefined) {
      throw new Error('Penpal bridge requires a browser environment');
    }
    return new WindowMessenger({
      remoteWindow: globalThis.window.parent,
      allowedOrigins: this.options.allowedOrigins ?? [globalThis.window.origin]
    });
  }
}
